import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { boundingBox, IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner';
import { CameraOff, Info, QrCode, Scan, Vibrate, VibrateOff, Volume2, VolumeOff } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ExtendedParticipationModal } from './extended-participation-modal';
import { useSettingsStore } from '@/stores/settingsStore';
import { SignalRContext } from '@/providers/SignalRProvider';
import expoImage from '@/assets/expo_main.jpg';
import storage from '@/utils/storage';
import { cn } from '@/utils';
import { useHasCameraPermission } from '@/hooks/use-has-camera-permission';
import { useMe } from '@/features/auth/api/get-me';
import { getLabelValue } from '@/utils/getLabelValue';
import successSound from '@/assets/scannerSuccessSound.mp3';
import errorSound from '@/assets/scannerErrorSound.mp3';

export function Reader() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [currentCode, setCurrentCode] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout>();
  const currentEventId = useSettingsStore(state => state.eventId);
  const direction = useSettingsStore(state => state.direction);
  const allowReEntry = useSettingsStore(state => state.allowReEntry);
  const onlyBooked = useSettingsStore(state => state.onlyBooked);
  const extendedParticipation = useSettingsStore(state => state.extendedParticipation);
  const vibrationEnabled = useSettingsStore(state => state.vibrationEnabled);
  const soundEnabled = useSettingsStore(state => state.soundEnabled);
  const toggleVibration = useSettingsStore(state => state.toggleVibration);
  const toggleSound = useSettingsStore(state => state.toggleSound);

  const isConnected = SignalRContext.connection?.state === 'Connected';

  const cameraAllowed = useHasCameraPermission();

  const { toast } = useToast();

  const { data: user } = useMe({});

  const playSound = (audioFile: string) => {
    const audio = new Audio(audioFile);
    audio.play();
  };

  const vibrate = (pattern: number | number[]) => {
    if (vibrationEnabled && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  SignalRContext.useSignalREffect(
    'onScanRejected',
    (message: string) => {
      setIsScanning(false);

      if (soundEnabled) playSound(errorSound);

      vibrate([200, 100, 200]);

      toast({ title: getLabelValue(message), variant: 'destructive' });
    },
    []
  );

  SignalRContext.useSignalREffect(
    'onScanSuccess',
    async (_, __, attendee) => {
      const fullName = `${attendee?.firstName} ${attendee?.lastName}, ${attendee?.companyName}`;

      if (soundEnabled) playSound(successSound);

      vibrate([200]);

      setIsScanning(false);
      toast({
        variant: 'success',
        body: (
          <div className="text-lg font-semibold">
            {fullName} {getLabelValue(direction === 'In' ? 'CheckedIn' : 'CheckedOut')}
          </div>
        ),
      });
    },
    []
  );

  const handleScan = async (codes: IDetectedBarcode[], force = false, extendedParticipationCount = 0) => {
    const data = codes?.[0];
    const format = data?.format;
    const badgeNo = data?.rawValue;

    resetTimeout();

    if (format !== 'qr_code') {
      return toast({
        variant: 'destructive',
        body: 'Felaktig QR-kod',
      });
    }

    if (extendedParticipation && !force) {
      setIsModalOpen(true);
      setCurrentCode(badgeNo);
      return;
    }

    try {
      setIsScanning(true);

      const { eventId, breakoutId: currentBreakoutId, direction: currentDirection, allowReEntry: currentAllowReEntry, alwaysPrint: currentAlwaysPrint, printer: currentPrinter, onlyBooked: currentOnlyBooked } = useSettingsStore.getState();

      await SignalRContext.invoke('scan', {
        eventId,
        badgeNo,
        passingType: currentDirection === 'In' ? 1 : 2,
        allowReEntry: currentAllowReEntry,
        printClient: currentAlwaysPrint ? currentPrinter?.clientGuid ?? null : null,
        breakoutId: currentBreakoutId ?? null,
        onlyBooked: currentOnlyBooked,
        userId: user?.userId,
        timeStamp: +new Date(),
        add: true,
        scanDevice: storage.getDeviceId(),
        exhibitorId: 0,
        extendedParticipation: extendedParticipationCount,
      });

      setCurrentCode('');
    } catch (e) {
      console.error(e);
      toast({
        variant: 'destructive',
        body: 'Fel vid scanning..',
      });
    }
  };

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setActive(false);
      setCurrentCode('');
      setIsModalOpen(false);
    }, 1000 * 120); // 2 minutes timeout
  };

  // Handle activating the scanner
  const handleActive = () => {
    resetTimeout(); // Reset the timeout when the scanner is activated
    setActive(true);
  };

  const handleError = (error: any) => {
    if (error === 'error NotAllowedError: Permission denied') {
      toast({
        variant: 'destructive',
        body: 'Kamera åtkomst nekad',
      });
    }
  };

  // Cleanup the timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (active) {
      setActive(false);
    }
  }, [allowReEntry, currentEventId, direction, extendedParticipation, onlyBooked]);

  const text = useMemo(() => {
    if (!cameraAllowed) {
      return 'Kamera åtkomst nekad';
    } else if (isConnected && !currentEventId) {
      return 'Välj event för att starta';
    } else if (isConnected && currentEventId) {
      return 'Tryck för att scanna';
    } else {
      return 'Ingen anslutning till servern';
    }
  }, [isConnected, currentEventId, cameraAllowed]);

  if (!active) {
    return (
      <div className="h-full w-full flex flex-col bg-black/80 text-white justify-center gap-8 items-center relative">
        <div
          className="w-full h-full absolute top-0 left-0 z-[-1]"
          style={{
            backgroundImage: `url(${expoImage})`,
            backgroundSize: 'cover',
          }}
        />
        <Button
          disabled={!isConnected || !currentEventId || !cameraAllowed}
          variant="ghost"
          className="h-auto relative hover:bg-transparent hover:text-white/60 text-white"
          onClick={handleActive}
        >
          <Scan className="w-[250px] h-[250px]  absolute stroke-[0.5px]" />
          <QrCode className="w-48 h-48 " />
        </Button>
        <div className="flex flex-col items-center gap-0">
          <p className="text-center text-lg text-white font-semibold">{text}</p>
          <div className=" text-sky-400 font-semibold p-2 rounded-md text-sm flex items-center gap-2"></div>
        </div>
        <div className="bg-transparent flex flex-col gap-4 absolute top-20 right-4">
          <Button className="w-12 h-12 flex items-center justify-center rounded-full" onClick={toggleSound}>
            {soundEnabled ? <Volume2 className="w-8 h-8" /> : <VolumeOff className="w-8 h-8" />}
          </Button>
          <Button className="w-12 h-12 flex items-center justify-center rounded-full" onClick={toggleVibration}>
            {vibrationEnabled ?  <Vibrate className="w-8 h-8" /> : <VibrateOff className="w-8 h-8" />}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full mx-auto flex flex-col justify-between">
      {isModalOpen && (
        <ExtendedParticipationModal
          badgeNo={currentCode}
          onClose={() => {
            setIsModalOpen(false);
            setCurrentCode('');
          }}
          onConfirm={(count: number) => {
            handleScan(
              [
                {
                  format: 'qr_code',
                  rawValue: currentCode,
                  boundingBox: { x: 0, y: 0, width: 0, height: 0 },
                  cornerPoints: [
                    { x: 0, y: 0 },
                    { x: 0, y: 0 },
                    { x: 0, y: 0 },
                    { x: 0, y: 0 },
                  ],
                },
              ],
              true,
              count
            );
            setIsModalOpen(false);
          }}
        />
      )}
      <div className={cn('h-full flex flex-col  bg-black/80 relative')}>
        <div className={cn('h-96 w-full mt-24 border-4 relative z-10 bg-transparent  border-teal-300 pointer-events-none', isScanning ? 'animate-pulse border-sky-300 ' : '')}>
          <Scanner
            classNames={{
              video: 'w-full !h-full absolute top-0 left-0',
              container: 'w-full !h-full',
            }}
            formats={['qr_code']}
            onScan={handleScan}
            onError={handleError}
            components={{
              audio: false,
              onOff: true,
              torch: true,
              zoom: true,
              finder: false,
              tracker: boundingBox,
            }}
            allowMultiple
            scanDelay={2000}
          />
        </div>
        <div className="p-4 flex justify-center  text-white flex-col items-center gap-2">
          <Button size="sm" onClick={() => setActive(false)}>
            <CameraOff className="w-4 h-4 mr-2" />
            Stäng av kamera
          </Button>
        </div>
      </div>
    </div>
  );
}
